import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { useAuth } from '../../Contexts/AuthContext';
import { useToast } from '../../Contexts/ToastContext';

const EmailIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
);

const inputIconStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '18px',
    transform: 'translateY(-50%)',
    color: '#999',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
};

const ForgotPassword: React.FC = () => {
    const { resetPassword } = useAuth();
    const { showToast } = useToast();

    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            showToast('Please enter your email address', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            await resetPassword(email.trim());
            setEmailSent(true);
            showToast('Password reset email sent! Check your inbox.', 'success');
        } catch (error: any) {
            const message =
                error?.code === 'auth/user-not-found'
                    ? 'No account found with this email address.'
                    : error?.code === 'auth/invalid-email'
                    ? 'Please enter a valid email address.'
                    : error?.code === 'auth/too-many-requests'
                    ? 'Too many requests. Please try again later.'
                    : error?.message || 'Failed to send password reset email. Please try again.';
            showToast(message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <Header />

            <section className="bg-img1 txt-center p-lr-15 p-tb-92" style={{ backgroundImage: `url('asset/images/bg-11.png')` }}>
                <h2 className="ltext-105 cl0 txt-center">Forgot Password</h2>
            </section>

            <section className="bg0 p-t-104 p-b-116">
                <div className="container">
                    <div className="flex-c-m">
                        <div className="size-210 bor10 p-lr-70 p-t-55 p-b-70 p-lr-15-lg w-full-md" style={{ maxWidth: 480 }}>
                            {emailSent ? (
                                <div className="txt-center">
                                    <h4 className="mtext-105 cl2 p-b-15">Check Your Email</h4>
                                    <p className="stext-115 cl6 p-b-30">
                                        We've sent a password reset link to <strong>{email}</strong>. Follow the
                                        link in that email to choose a new password.
                                    </p>
                                    <button
                                        type="button"
                                        className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer w-full"
                                        onClick={() => setEmailSent(false)}
                                    >
                                        Didn't get it? Try again
                                    </button>
                                    <p className="stext-115 cl6 txt-center p-t-30">
                                        <Link to="/login" className="cl1" style={{ fontWeight: 600 }}>
                                            Back to Sign In
                                        </Link>
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <h4 className="mtext-105 cl2 txt-center p-b-15">
                                        Reset Your Password
                                    </h4>
                                    <p className="stext-115 cl6 txt-center p-b-30">
                                        Enter the email address linked to your account and we'll send you a link
                                        to reset your password.
                                    </p>

                                    <div className="bor8 m-b-20 how-pos4-parent">
                                        <input
                                            className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                            type="email"
                                            placeholder="Your Email Address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            disabled={isSubmitting}
                                            autoComplete="email"
                                        />
                                        <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                            <EmailIcon />
                                        </span>
                                    </div>

                                    <button
                                        type="submit"
                                        className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer w-full"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                                    </button>

                                    <p className="stext-115 cl6 txt-center p-t-30">
                                        Remembered your password?{' '}
                                        <Link to="/login" className="cl1" style={{ fontWeight: 600 }}>
                                            Sign In
                                        </Link>
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ForgotPassword;
